import React from 'react';
import { FaFileExcel } from 'react-icons/fa';
import CheckboxMultiSelect from '../../../../../../../../components/common/CheckboxMultiSelect/CheckboxMultiSelect';
import CustomDatePicker from '../../../../../../../../components/common/CustomDatePicker/CustomDatePicker';
import { MPSwitch } from '../../../../../../../../components/ui';

interface FilterOption {
  value: string | number;
  label: string;
}

interface FilterBarProps {
  // Date filter props
  startDate: string;
  setStartDate: (date: string) => void;
  endDate: string;
  setEndDate: (date: string) => void;
  soloSesionActual: boolean;
  setSoloSesionActual: (value: boolean) => void;
  
  // Caja filter props
  cajaOptions: FilterOption[];
  selectedCajas: FilterOption[];
  onCajasChange: (selected: FilterOption[]) => void;
  
  // Action filter props (optional)
  actionOptions?: FilterOption[];
  selectedActions?: FilterOption[];
  onActionsChange?: (selected: FilterOption[]) => void;
  
  // User filter props
  userOptions: FilterOption[];
  selectedUsers: FilterOption[];
  onUsersChange: (selected: FilterOption[]) => void;
  
  // Transaction type filter props (Ingreso/Egreso)
  transactionTypeOptions: FilterOption[];
  selectedTransactionTypes: FilterOption[];
  onTransactionTypesChange: (selected: FilterOption[]) => void;
  
  // Amount range filter props (keeping the interface but not using them)
  enableAmountRange: boolean;
  setEnableAmountRange: (enabled: boolean) => void;
  amountRange: [number, number];
  setAmountRange: (range: [number, number]) => void;
  
  // Active filters (keeping the interface but not using them)
  activeFilters: any[];
  onRemoveFilter: (value: string) => void;
  onClearAllFilters: () => void;
  
  // Excel export props
  onExportToExcel?: () => void;
  exportDisabled?: boolean;
  totalMovements?: number;
}

const FilterBar: React.FC<FilterBarProps> = ({
  startDate,
  setStartDate,
  endDate,
  setEndDate,
  soloSesionActual,
  setSoloSesionActual,
  cajaOptions,
  selectedCajas,
  onCajasChange,
  actionOptions = [],
  selectedActions = [],
  onActionsChange = () => {},
  userOptions,
  selectedUsers,
  onUsersChange,
  transactionTypeOptions,
  selectedTransactionTypes,
  onTransactionTypesChange,
  enableAmountRange,
  setEnableAmountRange,
  amountRange,
  setAmountRange,
  activeFilters,
  onRemoveFilter,
  onClearAllFilters,
  onExportToExcel,
  exportDisabled = false,
  totalMovements = 0,
}) => {
  const handleStartDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setStartDate(e.target.value);
    if (soloSesionActual) {
      setSoloSesionActual(false);
    }
  };

  const handleEndDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEndDate(e.target.value);
    if (soloSesionActual) {
      setSoloSesionActual(false);
    }
  };

  const handleCurrentSessionToggle = () => {
    setSoloSesionActual(!soloSesionActual);
  };

  return (
    <div className="mp-toolbar">
      <div className="flex flex-wrap items-center gap-3">
        {/* Date pickers */}
        <div className="flex items-center gap-2 flex-shrink-0">
          <CustomDatePicker
            value={startDate}
            onChange={(d: string) => { handleStartDateChange({ target: { value: d } } as any); }}
            disabled={soloSesionActual}
            placeholder="Desde"
          />
          <span className="text-xs opacity-40" style={{ color: 'var(--text-color)' }}>Hasta:</span>
          <CustomDatePicker
            value={endDate}
            onChange={(d: string) => { handleEndDateChange({ target: { value: d } } as any); }}
            disabled={soloSesionActual}
            placeholder="Hasta"
          />
        </div>

        {/* Session toggle */}
        <MPSwitch label="Sesión actual" checked={soloSesionActual} onChange={(checked) => setSoloSesionActual(checked)} />

        {/* Dropdown filters */}
        <div className="flex items-center gap-2 flex-wrap">
          {cajaOptions.length > 1 && (
            <div className="min-w-[110px]">
              <CheckboxMultiSelect
                options={cajaOptions}
                value={selectedCajas}
                onChange={onCajasChange}
                placeholder="Cajas"
              />
            </div>
          )}
          <div className="min-w-[110px]">
            <CheckboxMultiSelect
              options={userOptions}
              value={selectedUsers}
              onChange={onUsersChange}
              placeholder="Usuarios"
            />
          </div>
          <div className="min-w-[110px]">
            <CheckboxMultiSelect
              options={transactionTypeOptions}
              value={selectedTransactionTypes}
              onChange={onTransactionTypesChange}
              placeholder="Tipo"
            />
          </div>
        </div>

        {/* Export */}
        {onExportToExcel && (
          <button
            className="mp-btn-export ml-auto"
            onClick={onExportToExcel}
            disabled={exportDisabled || totalMovements === 0}
          >
            <FaFileExcel />
            <span className="hidden sm:inline">Excel</span>
          </button>
        )}
      </div>
    </div>
  );
};

export default FilterBar;
